import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import PaginationComponent from '../components/PaginationComponent';
import TableStateCard from '../components/TableStateCard';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { scheduleDebouncedCallback, clearDebouncedCallback } from '../utils/pagination';
import {
    getCollection,
    getCollectionCandidates,
    addProductToCollection,
    removeProductFromCollection,
    normalizeCollection,
    normalizeLabeledProduct,
    normalizeCandidate,
    collectionCounts,
    visibilityBadge,
    motivoText,
    placementLabel,
    isPlaced,
    isNotFound,
} from '../utils/collectionsApi';

const PAGE_SIZE = 25;

// Curación de una colección de la home.
//
// Esta pantalla era un PickList de primereact que cargaba `product/all` —el catálogo ENTERO, sin
// paginar— y guardaba con un POST /label/labeledProducts que BORRABA todas las asignaciones y
// reinsertaba la lista completa. Dos problemas distintos:
//
//   1. La carga: miles de productos en el DOM. primereact 10.7 no puede virtualizar el PickList
//      (no expone virtualScrollerOptions), así que no había forma de arreglarlo sin sacarlo.
//   2. El guardado en bloque: el snapshot que reinsertaba lo había leído el navegador minutos
//      antes. Si alguien agregaba un producto a la colección en el interín, "Guardar" lo pisaba
//      en silencio. Envolverlo en una transacción no lo arregla: la transacción cubre la pérdida
//      por FALLO, no la pérdida por SNAPSHOT VIEJO.
//
// Por eso acá NO hay botón "Guardar": cada checkbox es un PUT/DELETE atómico e idempotente sobre
// UN producto, que se aplica al toque y no puede pisar el trabajo de nadie más.
class LabelAssignPage extends Component {

    constructor() {
        super();
        const cachedRole = Number(localStorage.getItem('role') || 0);
        this.state = {
            checked: false,
            role: cachedRole,

            labelId: null,
            collection: null,
            members: [],
            notFound: false,
            detailError: null,
            loadingDetail: true,

            candidates: [],
            currentPage: 1,
            quantityShowRows: PAGE_SIZE,
            totalItems: 0,
            search: '',
            searchInput: '',
            loadingCandidates: true,
            candidatesError: null,

            busy: {},
            rowErrors: {},
        };
        this.mounted = false;
        this.detailSeq = 0;
        this.candidatesSeq = 0;
    }

    async componentDidMount() {
        this.mounted = true;
        // validateSession(true): curar una colección no tiene nada que ver con la caja. Sin el
        // flag, un manager con la caja cerrada abre esta pantalla y lo patea a /caja sin explicación.
        const role = await validateSession(true);
        if (!this.mounted) return;

        const queryParams = new URLSearchParams(this.props.location.search);
        const rawId = queryParams.get('id');
        const labelId = Number(rawId);

        if (!rawId || !Number.isFinite(labelId) || labelId <= 0) {
            this.setState({ checked: true, role, labelId: null, notFound: true, loadingDetail: false, loadingCandidates: false });
            return;
        }

        this.setState({ checked: true, role, labelId });
        this.fetchDetail(labelId);
        this.fetchCandidates({ labelId });
    }

    componentWillUnmount() {
        this.mounted = false;
        clearDebouncedCallback(this, 'searchDebounceTimeout');
    }

    // ── Datos ────────────────────────────────────────────────────────────────────────────

    fetchDetail = (labelId = this.state.labelId, { silent = false } = {}) => {
        if (labelId == null) return Promise.resolve();
        const seq = ++this.detailSeq;
        if (!silent) {
            this.setState({ loadingDetail: true, detailError: null });
        }

        return getCollection(labelId)
            .then((data) => {
                // Descarta respuestas viejas: con toggles rápidos se dispara un refresco por cada
                // uno y una respuesta atrasada resucitaría un producto recién sacado.
                if (!this.mounted || seq !== this.detailSeq) return;

                const labelInfo = data && data.labelInfo;
                if (!labelInfo) {
                    // El backend viejo respondía error:0 con labelInfo:null para un id inexistente
                    // y la pantalla reventaba al leer labelInfo.name: pantalla en blanco, sin salida.
                    this.setState({ notFound: true, loadingDetail: false, collection: null, members: [] });
                    return;
                }

                const serverMembers = (data.labeledProducts || []).map(normalizeLabeledProduct);
                this.setState((prev) => ({
                    collection: normalizeCollection(labelInfo),
                    members: mergePending(serverMembers, prev.members, prev.busy),
                    notFound: false,
                    detailError: null,
                    loadingDetail: false,
                }));
            })
            .catch((error) => {
                if (!this.mounted || seq !== this.detailSeq) return;
                if (isNotFound(error)) {
                    this.setState({ notFound: true, loadingDetail: false, collection: null, members: [] });
                    return;
                }
                this.setState({ detailError: error.message, loadingDetail: false });
            });
    }

    fetchCandidates = ({
        labelId = this.state.labelId,
        page = this.state.currentPage,
        limit = this.state.quantityShowRows,
        search = this.state.search,
        showLoader = true,
    } = {}) => {
        if (labelId == null) return;
        const seq = ++this.candidatesSeq;
        if (showLoader) {
            this.setState({ loadingCandidates: true, candidatesError: null });
        } else {
            this.setState({ candidatesError: null });
        }

        getCollectionCandidates(labelId, { page, limit, search })
            .then((paginated) => {
                if (!this.mounted || seq !== this.candidatesSeq) return;
                this.setState({
                    candidates: (paginated.items || []).map(normalizeCandidate),
                    currentPage: paginated.page || Number(page),
                    quantityShowRows: paginated.limit || Number(limit),
                    totalItems: paginated.total || 0,
                    search,
                    loadingCandidates: false,
                });
            })
            .catch((error) => {
                if (!this.mounted || seq !== this.candidatesSeq) return;
                if (isNotFound(error)) {
                    this.setState({ notFound: true, loadingCandidates: false });
                    return;
                }
                // Sin este catch, un rechazo de red dejaba la tabla pegada en "Cargando…" para
                // siempre y sin forma de reintentar.
                this.setState({ candidatesError: error.message, loadingCandidates: false });
            });
    }

    // ── Membresía ────────────────────────────────────────────────────────────────────────

    memberKeys = () => {
        const keys = {};
        this.state.members.forEach((m) => { keys[String(m.code)] = true; });
        return keys;
    }

    isMember = (row, keys) => {
        const key = String(row.code);
        if (keys[key]) return true;
        // Si el detalle no pudo cargar, se cae al flag que manda el propio candidato. Es un
        // fallback: la fuente de verdad del checkbox es la lista de miembros del detalle.
        if (this.state.collection == null && row.inCollection !== undefined) return row.inCollection;
        return false;
    }

    // Alta/baja de UN producto, aplicada en el acto. Optimista en la UI (el checkbox no espera al
    // servidor) y con reversión si la llamada falla.
    toggleProduct = (row, next) => {
        const key = String(row.code);
        if (this.state.busy[key]) return;

        const memberRow = {
            code: row.code,
            name: row.name,
            visibleEnWeb: row.visibleEnWeb,
            motivo: row.motivo,
            labeledAt: row.labeledAt,
        };

        this.setState((prev) => ({
            busy: { ...prev.busy, [key]: true },
            rowErrors: omitKey(prev.rowErrors, key),
            members: next ? addMember(prev.members, memberRow) : removeMember(prev.members, key),
        }));

        const call = next
            ? addProductToCollection(this.state.labelId, row.code)
            : removeProductFromCollection(this.state.labelId, row.code);

        // `.then(ok, fail)` y no `.then(ok).catch(fail)`: con el catch encadenado, un error DENTRO
        // del handler de éxito se trataría como si hubiera fallado la llamada y revertiría un
        // cambio que el servidor sí aplicó.
        call.then(
            () => {
                if (!this.mounted) return;
                this.setState((prev) => ({ busy: omitKey(prev.busy, key) }));
                // Reconcilia contra el servidor: el alta optimista no sabe si el producto se ve o
                // no en la web (ese dato lo calcula el backend) ni recalcula los contadores.
                this.fetchDetail(this.state.labelId, { silent: true });
            },
            (error) => {
                if (!this.mounted) return;
                this.setState((prev) => ({
                    busy: omitKey(prev.busy, key),
                    members: next ? removeMember(prev.members, key) : addMember(prev.members, memberRow),
                    rowErrors: { ...prev.rowErrors, [key]: error.message },
                }));
            }
        );
    }

    // ── Handlers de UI ───────────────────────────────────────────────────────────────────

    onChangeSearch = (search) => {
        this.setState({ searchInput: search });
        scheduleDebouncedCallback(this, 'searchDebounceTimeout', () => {
            this.fetchCandidates({ page: 1, search, showLoader: false });
        });
    }

    onChangeShowRows = (event) => {
        this.fetchCandidates({ page: 1, limit: Number(event.target.value) });
    }

    goToEdit = () => {
        navigateTo(`/editLabel?id=${this.state.labelId}`);
    }

    // ── Render ───────────────────────────────────────────────────────────────────────────

    renderNotFound() {
        return (
            <div>
                <TableStateCard
                    icon="search_off"
                    title="Esa colección no existe"
                    message="Puede que la hayan borrado, o que el link esté mal. Volvé al listado y elegila de ahí."
                />
                <button type="button" className="btn btn-primary mt-3" onClick={() => navigateTo('/labels')}>
                    Volver a las colecciones
                </button>
            </div>
        );
    }

    renderSummary() {
        const collection = this.state.collection;
        if (!collection) return null;

        // Los asignados se cuentan de la lista que está justo abajo en la misma pantalla y no del
        // contador del servidor: mientras hay un alta en vuelo, el contador todavía dice el
        // número viejo y quedaban dos números distintos discutiendo en la misma pantalla.
        const asignados = this.state.members.length;
        const { visibles } = collectionCounts(collection, this.state.members);
        const ocultos = visibles != null ? Math.max(0, asignados - visibles) : 0;

        return (
            <div className="card card-small mb-4">
                <div className="card-body p-3">
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: '10px' }}>
                        <h5 className="m-0 mr-2">{collection.name}</h5>
                        {collection.slug ? <code style={{ fontSize: '12px' }}>{collection.slug}</code> : null}
                        <span className={`badge ${collection.isPublic ? 'badge-success' : 'badge-secondary'}`}>
                            {collection.isPublic ? 'Publicada en la web' : 'Privada (no se ve en la home)'}
                        </span>
                        <span className="badge badge-light" title="Lugar de la home donde se muestra">
                            {placementLabel(collection.placement)}
                        </span>
                        <div className="ml-auto">
                            <button type="button" className="btn btn-sm btn-white mr-1" onClick={() => navigateTo('/labels')}>
                                Volver al listado
                            </button>
                            <button type="button" className="btn btn-sm btn-white" onClick={this.goToEdit}>
                                Editar colección
                            </button>
                        </div>
                    </div>

                    <p className="m-0 mt-2" style={{ color: '#818ea3' }}>
                        {asignados} producto(s) en la colección
                        {visibles != null ? ` · ${visibles} se ven realmente en la web` : ''}
                        {ocultos > 0 ? ` · ${ocultos} no se ve(n): mirá la columna "En la web"` : ''}
                    </p>

                    {collection.isPublic && !isPlaced(collection.placement) ? (
                        <div className="alert alert-warning mt-2 mb-0 p-2" style={{ fontSize: '13px' }}>
                            Está publicada pero <strong>sin ubicación asignada</strong>: no aparece en ningún lugar de la home.
                            Asignale una ubicación desde <em>Editar colección</em>.
                        </div>
                    ) : null}

                    {/* "Publicada y todavía sin curar" es un estado normal, no una falla: la
                        tienda no dibuja la sección mientras no tenga productos, así que del lado
                        del cliente no queda ningún hueco. Se informa en gris. El amarillo queda
                        para lo que sí es un problema: hay productos curados y ninguno se ve. */}
                    {collection.isPublic && asignados === 0 ? (
                        <div className="alert alert-info mt-2 mb-0 p-2" style={{ fontSize: '13px' }}>
                            Todavía no curaste ningún producto. La tienda no muestra la sección hasta que tenga
                            al menos uno, así que mientras tanto el cliente no ve nada raro.
                        </div>
                    ) : null}

                    {collection.isPublic && asignados > 0 && visibles === 0 ? (
                        <div className="alert alert-warning mt-2 mb-0 p-2" style={{ fontSize: '13px' }}>
                            Está publicada y <strong>ninguno de los productos que curaste se ve en la web</strong>
                            {' '}(están ocultos o archivados): mirá la columna "En la web" acá abajo.
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }

    renderMembers() {
        const members = this.state.members;

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">En la colección ({members.length})</h6>
                </div>
                <div className="card-body p-3">
                    {members.length === 0 ? (
                        <p className="m-0" style={{ color: '#818ea3' }}>
                            Todavía no hay productos. Buscalos abajo y marcá el casillero: se agregan en el acto.
                        </p>
                    ) : (
                        <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                            {members.map((member) => {
                                const key = String(member.code);
                                const badge = visibilityBadge(member);
                                const busy = !!this.state.busy[key];
                                const error = this.state.rowErrors[key];
                                return (
                                    <div key={key} className="py-1" style={{ borderBottom: '1px solid #f1f3f5' }}>
                                        <div className="d-flex align-items-center">
                                            <div className="flex-grow-1">
                                                <span>{member.name}</span>
                                                <span style={{ color: '#818ea3', fontSize: '12px' }}> · {member.code}</span>
                                            </div>
                                            <span className={badge.className} title={badge.title} style={{ marginRight: '8px' }}>
                                                En la web: {badge.text}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-white text-danger"
                                                style={{ fontSize: '12px' }}
                                                disabled={busy}
                                                onClick={() => this.toggleProduct(member, false)}
                                            >
                                                {busy ? 'Sacando…' : 'Sacar'}
                                            </button>
                                        </div>
                                        {badge.text === 'No' && motivoText(member.motivo) ? (
                                            <div style={{ color: '#818ea3', fontSize: '12px' }}>{motivoText(member.motivo)}</div>
                                        ) : null}
                                        {error ? (
                                            <div className="text-danger" style={{ fontSize: '12px' }}>{error}</div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    renderCandidateRow(row, index, memberKeys) {
        const key = String(row.code);
        const checked = this.isMember(row, memberKeys);
        const busy = !!this.state.busy[key];
        const error = this.state.rowErrors[key];
        // `/candidates` puede no traer la visibilidad de cada fila (el contrato sólo la garantiza
        // en el detalle). Para los que ya están en la colección la sabemos por el detalle, así que
        // se prefiere ese dato antes que mostrar un "—" que no ayuda a nadie.
        const known = this.state.members.find((m) => String(m.code) === key);
        const badge = visibilityBadge(row.visibleEnWeb === undefined && known ? known : row);
        const motivo = motivoText(row.visibleEnWeb === undefined && known ? known.motivo : row.motivo);

        return (
            <tr role="row" className="odd" key={key} style={{ background: index % 2 === 0 ? '#ffffff' : '#fcfcfc' }}>
                <td tabIndex="0" className="sorting_1" style={{ width: '14%' }}>{row.code}</td>
                <td>
                    {row.name}
                    {error ? (
                        <div className="text-danger" style={{ fontSize: '12px' }}>
                            {error} — el casillero volvió a como estaba.
                        </div>
                    ) : null}
                </td>
                <td style={{ textAlign: 'center', width: '18%' }}>
                    <span className={badge.className} title={badge.title}>{badge.text}</span>
                    {badge.text === 'No' && motivo ? (
                        <div style={{ color: '#818ea3', fontSize: '12px' }}>{motivo}</div>
                    ) : null}
                </td>
                <td style={{ textAlign: 'center', width: '16%' }}>
                    <label className="m-0" style={{ cursor: busy ? 'wait' : 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={checked}
                            disabled={busy}
                            onChange={(e) => this.toggleProduct(row, e.target.checked)}
                        />
                        <span style={{ marginLeft: '6px', fontSize: '12px', color: '#818ea3' }}>
                            {busy ? 'Guardando…' : (checked ? 'En la colección' : 'Agregar')}
                        </span>
                    </label>
                </td>
            </tr>
        );
    }

    renderCandidates() {
        const collection = this.state.collection;
        const memberKeys = this.memberKeys();

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Buscar productos para agregar</h6>
                </div>
                <div className="card-body p-3">
                    <p className="m-0 mb-3" style={{ color: '#818ea3' }}>
                        Marcá o desmarcá el casillero y el cambio se guarda en el acto. No hay botón "Guardar":
                        así nunca se pisa lo que otra persona haya cambiado mientras tenías la pantalla abierta.
                        {collection && isPlaced(collection.placement)
                            ? ' Se listan sólo juegos y accesorios, que son los que la home sabe mostrar.'
                            : ''}
                    </p>

                    <div id="DataTables_Table_0_wrapper" className="dataTables_wrapper no-footer">
                        <div className="dataTables_length" id="DataTables_Table_0_length">
                            <label>Show
                            <select id="select_rows" value={this.state.quantityShowRows} onChange={this.onChangeShowRows}>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                            entries</label>
                        </div>
                        <div id="DataTables_Table_0_filter" className="dataTables_filter">
                            <label>Search:
                            <input
                                id="input_search"
                                type="search"
                                placeholder="Nombre o código"
                                value={this.state.searchInput}
                                onChange={(e) => this.onChangeSearch(e.target.value)}
                            />
                            </label>
                        </div>

                        <table className="transaction-history d-none dataTable no-footer dtr-inline" id="DataTables_Table_0" role="grid">
                            <thead>
                                <tr role="row">
                                    <th rowSpan="1" colSpan="1" style={{ width: '14%' }}>Código</th>
                                    <th rowSpan="1" colSpan="1">Producto</th>
                                    <th rowSpan="1" colSpan="1" style={{ width: '18%' }}>En la web</th>
                                    <th rowSpan="1" colSpan="1" style={{ width: '16%' }}>En la colección</th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.loadingCandidates ? (
                                    <tr key="loading"><td colSpan="4" style={{ textAlign: 'center', padding: '10px' }}>Cargando…</td></tr>
                                ) : this.state.candidatesError ? (
                                    <tr key="error"><td colSpan="4" style={{ textAlign: 'center', padding: '10px' }}>
                                        <span className="text-danger">{this.state.candidatesError}</span>
                                        <button type="button" className="btn btn-sm btn-white ml-2" onClick={() => this.fetchCandidates({})}>Reintentar</button>
                                    </td></tr>
                                ) : this.state.candidates.length === 0 ? (
                                    <tr key="empty"><td colSpan="4" style={{ textAlign: 'center', padding: '10px' }}>
                                        {this.state.searchInput || this.state.search
                                            ? `No se encontraron productos para "${this.state.searchInput || this.state.search}"`
                                            : 'No hay productos para asignar.'}
                                    </td></tr>
                                ) : (
                                    this.state.candidates.map((row, index) => this.renderCandidateRow(row, index, memberKeys))
                                )}
                            </tbody>
                        </table>

                        <PaginationComponent
                            totalItems={this.state.totalItems}
                            quantityRows={this.state.quantityShowRows}
                            currentPage={this.state.currentPage}
                            onPageChange={(page) => this.fetchCandidates({ page })}
                        />
                    </div>
                </div>
            </div>
        );
    }

    renderBody() {
        if (this.state.notFound) {
            return this.renderNotFound();
        }

        if (this.state.loadingDetail && this.state.collection == null) {
            return <TableStateCard loading />;
        }

        if (this.state.detailError && this.state.collection == null) {
            return (
                <TableStateCard
                    icon="error_outline"
                    title="No pudimos cargar la colección"
                    message={this.state.detailError}
                />
            );
        }

        return (
            <>
                {this.renderSummary()}
                {this.renderMembers()}
                {this.renderCandidates()}
            </>
        );
    }

    render() {
        if (!this.state.checked) {
            return (<div></div>);
        }

        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={15} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-9 col-sm-12 p-0 offset-lg-2 offset-md-3">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Etiquetas" subtitle="Curar una colección de la home" />
                            {this.renderBody()}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

function omitKey(map, key) {
    const copy = { ...map };
    delete copy[key];
    return copy;
}

function addMember(members, row) {
    const key = String(row.code);
    if (members.some((m) => String(m.code) === key)) {
        return members;
    }
    return [...members, row];
}

function removeMember(members, key) {
    return members.filter((m) => String(m.code) !== key);
}

// Un refresco del detalle que llega mientras hay altas/bajas EN VUELO no puede pisar el estado
// optimista de esos productos: el servidor todavía no vio el cambio. Sin esto, marcar dos
// casilleros rápido hacía que el refresco del primero desmarcara visualmente el segundo hasta
// que volviera su propia respuesta. Para todo lo demás manda el servidor.
function mergePending(serverMembers, localMembers, busy) {
    const pendingKeys = Object.keys(busy || {});
    if (pendingKeys.length === 0) {
        return serverMembers;
    }

    const merged = serverMembers.filter((m) => !busy[String(m.code)]);
    pendingKeys.forEach((key) => {
        const local = localMembers.find((m) => String(m.code) === key);
        if (local) {
            merged.push(local);
        }
    });
    return merged;
}

export default LabelAssignPage;
